import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Skeleton } from '@vehix/portal/components/ui/skeleton';
import { Calendar, Filter, XCircle } from 'lucide-react';
import { usePipelineSchedules } from '../../hooks/usePipelineOrchestrator';
import { useMinuteTick } from '../../hooks/useMinuteTick';
import { useScheduleRowActions } from '../../hooks/useScheduleRowActions';
import { PipelineSchedulesHealthStrip } from './PipelineSchedulesHealthStrip';
import { PipelineSchedulesTable } from './PipelineSchedulesTable';
import { PipelineSchedulesTimeline } from './PipelineSchedulesTimeline';
import { PipelineSchedulesToolbar, type ScheduleView } from './PipelineSchedulesToolbar';
import { ScheduleBulkActionsBar } from './ScheduleBulkActionsBar';
import { ScheduleDetailSheet } from './ScheduleDetailSheet';
import { ScheduleSettingsDialog } from './ScheduleSettingsDialog';
import { sharedRecipients } from '../../utils/scheduleRecipients';
import {
  buildScheduleRows,
  filterScheduleRows,
  sortScheduleRows,
  summarizeSchedules,
  DEFAULT_SCHEDULE_SORT,
  EMPTY_SCHEDULE_FILTERS,
  type ScheduleFilters,
  type ScheduleRow,
  type ScheduleSort,
  type ScheduleSortKey
} from '../../utils/scheduleRows';
import type {
  PipelineSchedule,
  PipelineScheduleListParams
} from '../../services/pipelineOrchestrator';

interface PipelineSchedulesListProps {
  tenantId?: string | undefined;
  onEdit: (schedule: PipelineSchedule) => void;
}

/** The reader's own timezone, resolved once — it can't change mid-session. */
const VIEWER_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const EmptyState: React.FC<{ icon: React.ReactNode; title: string; hint: React.ReactNode }> = ({
  icon,
  title,
  hint
}) => (
  <div className='py-12 text-center text-muted-foreground'>
    {icon}
    <p className='text-sm'>{title}</p>
    <div className='mt-1 text-xs'>{hint}</div>
  </div>
);

// eslint-disable-next-line max-lines-per-function
export const PipelineSchedulesList: React.FC<PipelineSchedulesListProps> = ({
  tenantId,
  onEdit
}) => {
  // Tenant is the ONLY server-side filter. Everything the toolbar offers is
  // applied client-side on purpose: adding a key here would fork the React
  // Query cache, costing a round-trip and a loading flash per toggle.
  const params: PipelineScheduleListParams = {
    ...(tenantId && { tenant_id: tenantId })
  };

  const { data, isLoading, error, refetch } = usePipelineSchedules(params);
  const actions = useScheduleRowActions();
  const now = useMinuteTick();

  const [filters, setFilters] = useState<ScheduleFilters>(EMPTY_SCHEDULE_FILTERS);
  const [sort, setSort] = useState<ScheduleSort>(DEFAULT_SCHEDULE_SORT);
  const [view, setView] = useState<ScheduleView>('table');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailRow, setDetailRow] = useState<ScheduleRow | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const schedules = useMemo(() => data?.schedules ?? [], [data]);

  // Predicting a next run is a cron walk, so it happens once per schedule per
  // minute here rather than per render in each view.
  const allRows = useMemo(() => buildScheduleRows(schedules, now), [schedules, now]);
  const rows = useMemo(
    () => sortScheduleRows(filterScheduleRows(allRows, filters), sort),
    [allRows, filters, sort]
  );
  const summary = useMemo(() => summarizeSchedules(rows), [rows]);

  // Derived from every schedule, not the filtered rows — recipients are a
  // property of the whole set, so a tenant filter must not narrow them.
  const shared = useMemo(() => sharedRecipients(schedules), [schedules]);

  const handleSort = (key: ScheduleSortKey) =>
    setSort(prev =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Select-all applies to the *visible* rows only, so it can never act on
  // something a filter is hiding.
  const toggleAll = useCallback(
    (select: boolean) => setSelectedIds(select ? new Set(rows.map(r => r.schedule.id)) : new Set()),
    [rows]
  );

  const selectedRows = useMemo(
    () => rows.filter(r => selectedIds.has(r.schedule.id)),
    [rows, selectedIds]
  );

  // Bulk work goes through the *Many helpers rather than a loop over the
  // single-row actions. React Query's mutation observer holds one callback
  // slot, so looping .mutate() fires every request but reports only the last
  // outcome — a cooldown or an in-flight conflict would vanish silently.
  const selectedSchedules = useMemo(() => selectedRows.map(r => r.schedule), [selectedRows]);
  const bulk = {
    pause: () => void actions.setEnabledMany(selectedSchedules, false),
    resume: () => void actions.setEnabledMany(selectedSchedules, true),
    runNow: () => void actions.triggerMany(selectedSchedules)
  };

  const openDetail = useCallback((row: ScheduleRow) => setDetailRow(row), []);

  if (error) {
    return (
      <div className='py-8 text-center'>
        <XCircle className='mx-auto mb-2 h-8 w-8 text-red-500' />
        <p className='text-sm font-medium'>Couldn&apos;t load schedules</p>
        <p className='mt-1 text-xs text-muted-foreground'>
          {error instanceof Error ? error.message : 'The pipeline service did not respond.'}
        </p>
        <Button variant='outline' size='sm' className='mt-3' onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
          {[0, 1, 2, 3].map(i => (
            <Skeleton key={i} className='h-16' />
          ))}
        </div>
        <Skeleton className='h-8 w-full max-w-md' />
        <Skeleton className='h-64 w-full' />
      </div>
    );
  }

  // No schedules at all — distinct from "filters hid them", which is the
  // classic conflation that makes people think the page broke.
  if (schedules.length === 0) {
    return (
      <EmptyState
        icon={<Calendar className='mx-auto mb-2 h-8 w-8 opacity-40' />}
        title={tenantId ? 'No schedules for this tenant' : 'No pipeline schedules yet'}
        hint='Use Create Schedule above to automate pipeline runs.'
      />
    );
  }

  return (
    <div className='space-y-4'>
      <PipelineSchedulesHealthStrip summary={summary} now={now} />

      <PipelineSchedulesToolbar
        filters={filters}
        onFiltersChange={setFilters}
        view={view}
        onViewChange={setView}
        shown={rows.length}
        total={schedules.length}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      <ScheduleBulkActionsBar
        count={selectedRows.length}
        activeCount={selectedRows.filter(r => r.schedule.is_enabled).length}
        onPause={bulk.pause}
        onResume={bulk.resume}
        onRunNow={bulk.runNow}
        onClear={() => setSelectedIds(new Set())}
      />

      {rows.length === 0 ? (
        <EmptyState
          icon={<Filter className='mx-auto mb-2 h-8 w-8 opacity-40' />}
          title='No schedules match these filters'
          hint={
            <Button variant='outline' size='sm' onClick={() => setFilters(EMPTY_SCHEDULE_FILTERS)}>
              Clear filters
            </Button>
          }
        />
      ) : view === 'table' ? (
        <PipelineSchedulesTable
          rows={rows}
          now={now}
          viewerZone={VIEWER_ZONE}
          sort={sort}
          onSort={handleSort}
          onEdit={onEdit}
          onToggleEnabled={actions.toggleEnabled}
          onTrigger={actions.triggerNow}
          onDelete={actions.remove}
          isCoolingDown={actions.isCoolingDown}
          onOpenDetail={openDetail}
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          onToggleAll={toggleAll}
        />
      ) : (
        <PipelineSchedulesTimeline
          rows={rows}
          now={now}
          viewerZone={VIEWER_ZONE}
          onSelect={openDetail}
        />
      )}

      <ScheduleSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        schedules={schedules}
        shared={shared}
        onSave={actions.setRecipientsForMany}
      />

      <ScheduleDetailSheet
        row={detailRow}
        now={now}
        open={detailRow !== null}
        onOpenChange={open => !open && setDetailRow(null)}
        onEdit={schedule => {
          setDetailRow(null);
          onEdit(schedule);
        }}
        onTrigger={actions.triggerNow}
        onDelete={actions.remove}
        isCoolingDown={actions.isCoolingDown}
      />
    </div>
  );
};
